import React, { useEffect, useState, useCallback } from 'react';
import { Select } from 'antd';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { fetchActiveCustomers } from '@/redux/actions/customerActions';
import { repeatPhoneNumber } from '@/utils/helpers';
import { checkPermission } from '@/utils/permission';

/**
 * CustomerSelect Component (Function Component + Redux Hooks)
 *
 * A reusable customer selection dropdown using Ant Design's Select component.
 * Supports dynamic search, infinite scroll, and formatting based on permissions.
 *
 * Tính năng chính:
 * - Tìm kiếm khách hàng theo tên, mã hoặc số điện thoại (3 số cuối)
 * - Tự động load thêm khách hàng khi scroll xuống cuối danh sách (pagination)
 * - Label hiển thị dạng: "Tên khách hàng (Mã, SĐT hoặc *** che nếu không có quyền)"
 * - Hỗ trợ đầu vào dạng:
 *    - `{ value, label }` (chuẩn `labelInValue`)
 *    - `{ id, full_name, code }` (tự động chuyển đổi)
 * - Nếu khách hàng được chọn không có trong danh sách `options`, sẽ được thêm tạm để giữ selected
 *
 * Kết nối Redux thông qua:
 * - `useSelector()` để lấy danh sách khách hàng & phân trang
 * - `useDispatch()` để gọi `fetchActiveCustomers({ keyword, page })`
 *
 * Component tương thích tốt với Form.create từ Ant Design (`getFieldDecorator`)
 *
 * @component
 *
 * @example
 * <Form.Item label="Khách hàng">
 *   {getFieldDecorator('customer_id', {
 *     rules: [{ required: true, message: 'Trường bắt buộc!' }],
 *     initialValue: currentItem?.customer ?? null
 *   })(
 *     <CustomerSelect
 *       onChange={(id, label) => setSelectedCustomer(id, label)}
 *     />
 *   )}
 * </Form.Item>
 *
 * @param {object} props
 * @param {object} props.value - Giá trị hiện tại của Select. Dạng `{ value, label }` hoặc `{ id, full_name, code }`
 * @param {function} props.onChange - Callback khi chọn hoặc xoá khách hàng. Trả về `(id, label)`
 * @param {string} [props.defaultText] - Hiển thị label mặc định nếu muốn (vd: "Chọn khách hàng")
 * @param {boolean} [props.isLoading=false] - Trạng thái loading từ phía ngoài (tùy chọn)
 *
 * Redux sử dụng:
 * - state.customerReducer.activeList.items → danh sách khách hàng
 * - state.customerReducer.activeList.pagination → dữ liệu phân trang
 * - state.customerReducer.activeList.loading → trạng thái loading
 *
 * Ghi chú:
 * - Component dùng `labelInValue` ⇒ `value` phải là object `{ value, label }`
 * - Sử dụng `checkPermission()` để hiển thị hoặc ẩn số điện thoại thật
 */
const CustomerSelect = ({
    value,
    onChange,
    defaultText,
    isLoading: propLoading = false,
    ...restProps
}) => {
    const dispatch = useDispatch();

    const [keyword, setKeyword] = useState('');

    const options = useSelector(state => state.customerReducer.activeList.items);
    const pagination = useSelector(state => state.customerReducer.activeList.pagination);

    const isLoading = useSelector(state => state.customerReducer.activeList.loading) || propLoading;

    useEffect(() => {
        dispatch(fetchActiveCustomers({ keyword: '', page: 1 }));
    }, [dispatch]);

    const handleSearch = useCallback((val) => {
        setKeyword(val);
        dispatch(fetchActiveCustomers({ keyword: val }));
    }, [dispatch]);

    const handleScroll = useCallback((e) => {
        e.preventDefault();
        //if (pagination?.currentPage && pagination?.currentPage < pagination?.totalPages) {
        dispatch(fetchActiveCustomers({ keyword, page: pagination.currentPage + 1 }));
        //}
    }, [dispatch, keyword, pagination]);

    const formatLabel = (option) => {
        const name = option.full_name ?? '';
        const code = option.code ?? '';
        const phone = option.phones?.find((p) => p.is_primary)?.phone ?? '';

        let extra = code;
        if (phone) {
            extra += ', ' + (checkPermission('customer', 'phonenumber') ? phone : repeatPhoneNumber(phone));
        }

        return extra ? `${name} (${extra})` : name;
    };

    // Xử lý merge value nếu không nằm trong danh sách
    const selectedId = value?.value;
    const selectedLabel = value?.label;
    const exists = options.find((opt) => opt.id === selectedId);
    const mergedOptions = exists || !selectedId
        ? options
        : [{ id: selectedId, full_name: selectedLabel }].concat(options);

    return (
        <Select
            showSearch
            allowClear
            labelInValue
            value={value ?? null}
            onChange={(selected) => {
                if (onChange) {
                    onChange(selected?.value ?? null, selected?.label ?? null);
                }

                // Nếu clear -> reset keyword và fetch lại danh sách
                if (selected === null) {
                    setKeyword('');
                    dispatch(fetchActiveCustomers({ keyword: '', page: 1 }));
                }
            }}
            onSearch={handleSearch}
            onPopupScroll={handleScroll}
            filterOption={false}
            loading={isLoading}
            placeholder="Mã, tên, 3 số cuối sđt của khách hàng"
            options={[
                ...(defaultText ? [{ value: null, label: defaultText }] : []),
                ...mergedOptions.map((option, index) => ({
                    key: `${option.id}_${index}`,
                    value: option.id,
                    label: formatLabel(option),
                })),
            ]}
            {...restProps}
        />
    );
};

CustomerSelect.propTypes = {
    value: PropTypes.any,
    onChange: PropTypes.func.isRequired,
    defaultText: PropTypes.string,
    isLoading: PropTypes.bool,
};

export default CustomerSelect;


// import React, { Component } from 'react';
// import { Select } from 'antd';
// import PropTypes from 'prop-types';
// import { connect } from 'react-redux';
// import { fetchActiveCustomers } from '@/redux/actions/customerActions';
// import { repeatPhoneNumber } from '@/utils/helpers';
// import { checkPermission } from '@/utils/permission';


// /**
//  * CustomerSelect Component
//  *
//  * A Redux-connected Ant Design Select component for choosing active customers with
//  * dynamic search, infinite scroll, and support for flexible input value formats.
//  *
//  * Đặc điểm chính:
//  * - Hỗ trợ tìm kiếm khách hàng theo tên, mã, hoặc số điện thoại
//  * - Tự động nạp thêm khách hàng khi scroll đến cuối danh sách (pagination)
//  * - Tự sinh label hiển thị gồm tên, mã KH, và số điện thoại (có che số nếu không có quyền)
//  * - Chấp nhận giá trị đầu vào linh hoạt:
//  *   - `{ value, label }` (chuẩn `labelInValue`)
//  *   - `{ id, full_name, code }` (tự convert nội bộ)
//  * - Nếu khách hàng được chọn không nằm trong `options`, component sẽ thêm tạm thời để giữ selected
//  *
//  * Sử dụng đi kèm với Ant Design Form (`getFieldDecorator`)
//  *
//  * @component
//  *
//  * @example
//  * <Form.Item label="Khách hàng">
//  *   {getFieldDecorator('customer_id', {
//  *     rules: [{ required: true, message: 'Trường bắt buộc!' }],
//  *     initialValue: currentItem?.customer ?? null
//  *   })(
//  *     <CustomerSelect
//  *       onChange={(id, label) => this.setSelectedCustomer(id, label)}
//  *     />
//  *   )}
//  * </Form.Item>
//  *
//  * @prop {object} value - Giá trị hiện tại của Select. Có thể là `{ value, label }` hoặc `{ id, full_name, code }`
//  * @prop {function} onChange - Callback khi chọn hoặc xoá khách hàng. Trả về `(id, label)`
//  * @prop {string} [defaultText] - Tuỳ chọn để hiển thị label mặc định như "Chọn khách hàng"
//  * @prop {boolean} [isLoading=false] - Trạng thái loading khi đang fetch khách hàng
//  * @prop {array} options - Danh sách khách hàng từ Redux (map từ `state.customerReducer.activeList.items`)
//  * @prop {object} pagination - Dữ liệu phân trang khách hàng (từ `state.customerReducer.activeList.pagination`)
//  *
//  * Redux Dependencies:
//  * - state.customerReducer.activeList.items
//  * - state.customerReducer.activeList.pagination
//  *
//  * Redux Actions:
//  * - fetchActiveCustomers({ keyword, page })
//  *
//  * Lưu ý kỹ thuật:
//  * - Component sử dụng `labelInValue`, nên `value` phải là object `{ value, label }`
//  * - Nếu khách hàng được chọn không nằm trong `options`, sẽ được merge vào `mergedOptions`
//  * - Sử dụng `checkPermission()` để ẩn hoặc hiện đầy đủ số điện thoại
//  */


// class CustomerSelect extends Component {
//     static propTypes = {
//         selected: PropTypes.any,
//         options: PropTypes.array.isRequired,
//         isLoading: PropTypes.bool,
//         pagination: PropTypes.object,
//         onChange: PropTypes.func.isRequired,
//         defaultText: PropTypes.string
//     };

//     static defaultProps = {
//         isLoading: false,
//         defaultText: null
//     };

//     state = {
//         keyword: ''
//     };

//     componentDidMount() {
//         this.props.fetchActiveCustomers({ keyword: '', page: 1 });
//     }

//     handleSearch(val) {
//         this.setState({ keyword: val }, () => {
//             this.props.fetchActiveCustomers({ keyword: val });
//         })
//     }

//     handleScroll(e) {
//         e.preventDefault();
//         this.props.fetchActiveCustomers({ keyword: this.state.keyword, page: this.props.pagination.currentPage + 1 });
//     }

//     formatLabel = (option) => {
//         let label = option.full_name ?? '';
//         const primaryPhoneObj = option.phones?.find(p => p.is_primary === 1);
//         const phone = primaryPhoneObj?.phone ?? '';
//         const code = option.code ?? '';

//         let additional = '';
//         if (phone) {
//             if (checkPermission('customer', 'phonenumber')) {
//                 additional = `${code}, ${phone}`;
//             } else {
//                 additional = `${code}, ${repeatPhoneNumber(phone)}`;
//             }
//         } else {
//             additional = code;
//         }

//         return additional ? `${label} (${additional})` : label;
//     };

//     render() {
//         const {
//             value,                // Được truyền từ getFieldDecorator
//             options,              // Danh sách khách hàng đang hiển thị
//             defaultText,
//             isLoading,
//         } = this.props;

//         // Kiểm tra nếu khách hàng đang được chọn có nằm trong options hay chưa
//         const selectedId = value?.value;
//         const selectedLabel = value?.label;

//         const exists = options.find(opt => opt.id === selectedId);

//         // Nếu chưa có thì tạo bản ghi tạm từ value (chỉ cần id và label)
//         const mergedOptions = exists || !selectedId
//             ? options
//             : [{ id: selectedId, full_name: selectedLabel }].concat(options);

//         return (
//             <Select
//                 {...this.props}
//                 showSearch
//                 allowClear
//                 labelInValue
//                 value={value ?? null} // { value: 123, label: "Tên KH" }
//                 onChange={(selected) => {
//                     if (this.props.onChange) {
//                         this.props.onChange(selected?.value ?? null, selected?.label ?? null);
//                     }

//                     // Nếu clear -> reset keyword và fetch lại danh sách
//                     if (selected === null) {
//                         this.setState({ keyword: '' });
//                         this.props.fetchActiveCustomers({ keyword: '', page: 1 });
//                     }
//                 }}
//                 onSearch={(val) => this.handleSearch(val)}
//                 onPopupScroll={(e) => this.handleScroll(e)}
//                 filterOption={false}
//                 loading={isLoading}
//                 placeholder="Mã, tên, 3 số cuối sđt của khách hàng"
//                 options={[
//                     ...(defaultText ? [{ value: null, label: defaultText }] : []),
//                     ...mergedOptions.map((option, index) => ({
//                         key: `${option.id}_${index}`,
//                         value: option.id,
//                         label: this.formatLabel(option),
//                     }))
//                 ]}
//             />
//         );
//     }
// }

// const mapStateToProps = (state) => ({
//     options: state.customerReducer.activeList.items,
//     pagination: state.customerReducer.activeList.pagination
// });

// const mapDispatchToProps = {
//     fetchActiveCustomers: (filter) => fetchActiveCustomers(filter),
// };

// export default connect(mapStateToProps, mapDispatchToProps)(CustomerSelect);
